import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { redo, undo } from "@codemirror/commands";
import { getSearchQuery, openSearchPanel, search } from "@codemirror/search";
import { EditorView, keymap } from "@codemirror/view";
import styled from "styled-components";

import Dropdown from "components/atoms/Dropdown";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import ManuscriptMathOCRModal from "components/organisms/manuscript/editor/ai/math-ocr/ManuscriptMathOCRModal";
import { useMathOCRWebSocket } from "components/organisms/manuscript/editor/ai/math-ocr/useMathOCRWebSocket";
import ManuscriptTextEditorHost from "components/organisms/manuscript/editor/ManuscriptTextEditorHost";
import {
    compileAnnotations,
    setAnnotations as setCompileAnnotations,
    showCompileLogDiagnostics,
} from "components/organisms/manuscript/pdf/compile-annotations";
import { selectionActionContextMenu } from "components/organisms/manuscript/editor/SelectionContextMenu";
import ManuscriptEditorToolbar from "components/organisms/manuscript/editor/toolbar/ManuscriptEditorToolbar";
import ManuscriptFigureModal from "components/organisms/manuscript/editor/visual/figure/ManuscriptFigureModal";
import { reviewThreadClickExtension } from "components/organisms/manuscript/review/codemirror";
import { buildCodePresenceExtension } from "components/organisms/manuscript/editor/code-presence";
import { metadataState, setMetadata, setSyntaxValidation } from "components/organisms/manuscript/editor/latex/language";
import { latexSyntaxErrorClickExtension } from "components/organisms/manuscript/editor/latex/linter/latex-linter";
import { manuscriptVisual, setVisual } from "components/organisms/manuscript/editor/visual";
import {
    isExternalGraphicsPath,
    normalizeGraphicsPath,
    resolveFigurePreviewNode,
} from "components/organisms/manuscript/editor/visual/figure/graphics-preview";
import {
    createDefaultFigureDraft,
    editFigureData,
    editFigureDataEffect,
    FigureModalSource,
    inferFigureTextFromPath,
    waitForFileTreeUpdate,
} from "components/organisms/manuscript/editor/visual/figure/figure-codemirror";
import { autoComplete } from "components/organisms/editor/codemirror/autocomplete";
import { manuscriptLatex } from "components/organisms/manuscript/editor/latex";
import { manuscriptSearchPhrases } from "components/organisms/manuscript/editor/search-i18n";

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    background: #ffffff;
    border-right: 1px solid #d8e1ec;
    overflow: hidden;
`;

const EditorToolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 34px;
    padding: 0 10px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #24384d;
`;

const ToolbarGroup = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
`;

const ToolbarInline = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    width: max-content;
`;

const ToolbarOverflowSpacer = styled.div`
    flex: 1;
    min-width: 0;
`;

const OverflowAnchor = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
    width: ${(props) => (props.$visible ? "54px" : "0px")};
    overflow: hidden;
    transition: width 120ms ease-out;
`;

const OverflowMenu = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border: 1px solid #d8e1ec;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
`;

const LocationBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 6px;
    border-bottom: 1px solid #dfe7ef;
    background: #f5f8fb;
    overflow: auto;
`;

const LocationSegment = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    height: 28px;
    padding: 0 6px;
    border: 0;
    border-radius: 4px;
    background: ${(props) => (props.$active ? "#e8eef5" : "transparent")};
    color: ${(props) => (props.$active ? "#18324a" : "#5f7388")};
    cursor: ${(props) => (props.onClick ? "pointer" : "default")};
`;

const LocationLabel = styled.span`
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 600;
`;

const LocationDivider = styled.span`
    color: #8ea0b3;
    font-size: 10px;
`;

const EditorArea = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    background:
        radial-gradient(circle at top, rgba(226, 235, 244, 0.45), transparent 42%),
        linear-gradient(180deg, #edf3f8 0%, #eef4f7 100%);

    .cm-editor {
        height: 100%;
        background: #ffffff;
        color: #17314a;
        font-size: 14px;
    }

    .cm-theme {
        height: 100%;
    }

    .cm-scroller {
        height: 100% !important;
        overflow: auto !important;
        font-family: "SFMono-Regular", "Consolas", "Liberation Mono", monospace;
        line-height: 1.7;
    }

    .cm-gutters {
        background: #f7f9fc;
        border-right: 1px solid #e1e7ef;
        color: #94a3b8;
    }

    .cm-activeLine,
    .cm-activeLineGutter {
        background: #eef4f8;
    }

    .cm-content {
        padding: 16px 0;
    }

    .cm-line {
        padding: 0 16px;
    }

    .cm-searchMatch {
        background: rgba(253, 224, 71, 0.3);
        box-shadow: inset 0 0 0 1px rgba(202, 138, 4, 0.35);
        border-radius: 2px;
    }

    .cm-searchMatch.cm-searchMatch-selected {
        background: rgba(15, 143, 111, 0.26);
        box-shadow: inset 0 0 0 1px rgba(15, 143, 111, 0.55);
    }

    .cm-panels {
        border-bottom: 1px solid #dfe7ef;
        background: #f8fafc;
        color: #32485e;
    }

    .cm-panels .cm-panel {
        padding: 6px 8px;
        gap: 6px;
        font-size: 12px;
    }

    .cm-panels .cm-search {
        display: grid;
        align-items: center;
        grid-template-columns: max-content max-content max-content max-content max-content max-content max-content max-content 1fr max-content;
        column-gap: 6px;
        row-gap: 6px;
    }

    .cm-panels .cm-search::after {
        content: attr(data-match-count);
        grid-row: 1;
        grid-column: 2;
        margin: 0 2px 0 0;
        padding: 0 2px;
        color: #5b7086;
        font-size: 11px;
        font-weight: 700;
    }

    .cm-panels .cm-search input {
        height: 24px;
        padding: 0 8px;
        border: 1px solid #cfdae7;
        border-radius: 4px;
        background: #ffffff;
        color: #24384d;
        outline: none;
    }

    .cm-panels .cm-search input:focus {
        border-color: #0f8f6f;
        box-shadow: 0 0 0 2px rgba(15, 143, 111, 0.12);
    }

    .cm-panels .cm-search button {
        height: 24px;
        padding: 0 8px;
        border: 1px solid #d2dce7;
        border-radius: 4px;
        background: #ffffff;
        color: #3a5168;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
    }

    .cm-panels .cm-search button:hover {
        background: #eef3f8;
    }

    .cm-panels .cm-search button[name="prev"] {
        grid-row: 1;
        grid-column: 3;
    }

    .cm-panels .cm-search button[name="next"] {
        grid-row: 1;
        grid-column: 4;
    }

    .cm-panels .cm-search button[name="select"] {
        grid-row: 1;
        grid-column: 5;
    }

    .cm-panels .cm-search input[name="search"] {
        grid-row: 1;
        grid-column: 1;
    }

    .cm-panels .cm-search label:nth-of-type(1) {
        grid-row: 1;
        grid-column: 6;
    }

    .cm-panels .cm-search label:nth-of-type(2) {
        grid-row: 1;
        grid-column: 7;
    }

    .cm-panels .cm-search label:nth-of-type(3) {
        grid-row: 1;
        grid-column: 8;
    }

    .cm-panels .cm-search button[name="close"] {
        grid-row: 1;
        grid-column: 10;
        justify-self: end;
    }

    .cm-panels .cm-search > br {
        display: none;
    }

    .cm-panels .cm-search input[name="replace"] {
        grid-row: 2;
        grid-column: 1 / span 3;
        min-width: 220px;
    }

    .cm-panels .cm-search button[name="replace"] {
        grid-row: 2;
        grid-column: 4;
    }

    .cm-panels .cm-search button[name="replaceAll"] {
        grid-row: 2;
        grid-column: 5;
    }

    .cm-panels .cm-search input[name="replace"],
    .cm-panels .cm-search button[name="replace"],
    .cm-panels .cm-search button[name="replaceAll"] {
        margin-top: 2px;
    }

    .cm-panels .cm-search label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: #4f6479;
    }
`;

const Placeholder = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 32px;
    color: #4d6178;
    text-align: center;
`;

const ImageViewer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 28px;
    background:
        linear-gradient(45deg, #f6f8fb 25%, transparent 25%), linear-gradient(-45deg, #f6f8fb 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #f6f8fb 75%), linear-gradient(-45deg, transparent 75%, #f6f8fb 75%);
    background-size: 24px 24px;
    background-position:
        0 0,
        0 12px,
        12px -12px,
        -12px 0px;
`;

const ImageFrame = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    max-height: 100%;
    padding: 18px;
    border: 1px solid #d8e1ec;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
`;

const PreviewImage = styled.img`
    display: block;
    max-width: min(100%, 960px);
    max-height: min(100%, 720px);
    object-fit: contain;
`;

const StatusBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 28px;
    padding: 0 10px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 10px;
    color: #64748b;
`;

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 24px;
    padding: 0 7px;
    border: 1px solid ${(props) => (props.$primary ? "#12805f" : props.$active ? "#c9d6e3" : "#d9e2ec")};
    border-radius: 4px;
    background: ${(props) => (props.$primary ? "#138a6d" : props.$active ? "#f3f7fb" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : props.$active ? "#20364b" : "#42576b")};
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const Status = styled.div`
    padding: 0;
    background: transparent;
    color: #54677c;
    font-size: 10px;
    font-weight: 600;
`;

const MetaList = styled.div`
    display: grid;
    grid-template-columns: minmax(88px, 110px) minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 13px;
`;

const MetaKey = styled.div`
    font-weight: 700;
    color: #29415d;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const METADATA_LABEL_RE = /\\label\{([^}]+)\}/g;
const METADATA_PACKAGE_RE = /\\(?:usepackage|RequirePackage)(?:\[[^\]]*])?\{([^}]+)\}/g;
const METADATA_BIBITEM_RE = /\\bibitem(?:\[[^\]]*])?\{([^}]+)\}/g;

function extractCommandArgument(commandText) {
    const start = commandText.indexOf("{");
    const end = commandText.lastIndexOf("}");
    if (start < 0 || end <= start) {
        return "";
    }
    return commandText.slice(start + 1, end);
}

function isSvgFile(path) {
    return /\.svg$/i.test(path || "");
}

function collectCompletionMetadata(content = "") {
    const labels = new Set();
    const packageNames = new Set();
    const referenceKeys = new Set();

    for (const match of content.matchAll(METADATA_LABEL_RE)) {
        const label = String(match[1] || "").trim();
        if (label) {
            labels.add(label);
        }
    }

    for (const match of content.matchAll(METADATA_PACKAGE_RE)) {
        String(match[1] || "")
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
            .forEach((name) => packageNames.add(name));
    }

    for (const match of content.matchAll(METADATA_BIBITEM_RE)) {
        const key = String(match[1] || "").trim();
        if (key) {
            referenceKeys.add(key);
        }
    }

    return {
        labels,
        packageNames,
        referenceKeys,
        commands: [],
    };
}

function ManuscriptEditorPanel({
    spaceUuid,
    manuscriptId,
    selectedNode,
    setToast,
    uploadBinaryFile,
    importFigureUrl,
    saveLabel,
    onSave,
    saveDisabled,
    selection,
    editorContent,
    onEditorChange,
    onSelectionChange,
    onActiveLineChange,
    editorRef,
    currentVersion,
    currentEditorMode,
    availableEditorModes,
    onEditorModeChange,
    currentLocationPath,
    onLocationSelect,
    onOpenSelectionActionMenu,
    onLatexSyntaxErrorClick,
    onSelectReviewThread,
    externalCommand,
    manuscriptTreeData,
    realtimeExtension,
    remoteCursors = [],
    compileLogAnnotations = [],
    compiling = false,
    editedSinceCompileStarted = false,
    syntaxValidation = true,
    readOnly = false,
}) {
    const toolbarViewportRef = useRef(null);
    const toolbarInlineRef = useRef(null);
    const toolbarOverflowFrameRef = useRef(null);
    const [overflowGroups, setOverflowGroups] = useState(new Set());
    const [overflowOpen, setOverflowOpen] = useState(false);
    const [currentEditorView, setCurrentEditorView] = useState(null);
    const previousEditorModeRef = useRef(currentEditorMode);

    function updateSearchMatchCounter(view) {
        const panel = view?.dom?.querySelector(".cm-search");
        if (!panel) return;

        const query = getSearchQuery(view.state);
        if (!query?.valid || !query.search) {
            panel.setAttribute("data-match-count", "0/0");
            return;
        }

        const mainSelection = view.state.selection.main;
        let total = 0;
        let current = 0;
        let containsCurrent = 0;

        for (const match of query.getCursor(view.state)) {
            total += 1;
            if (mainSelection.from === match.from && mainSelection.to === match.to) {
                current = total;
            }
            if (containsCurrent === 0 && mainSelection.head >= match.from && mainSelection.head <= match.to) {
                containsCurrent = total;
            }
        }

        if (current === 0) {
            current = containsCurrent;
        }
        panel.setAttribute("data-match-count", `${current}/${total}`);
    }

    function scheduleSearchMatchCounter(view) {
        window.requestAnimationFrame(() => {
            updateSearchMatchCounter(view);
        });
    }

    const buildToolbarOverflow = useCallback(() => {
        const viewport = toolbarViewportRef.current;
        const inline = toolbarInlineRef.current;
        if (!viewport || !inline) {
            return;
        }

        const buttonGroups = Array.from(inline.querySelectorAll("[data-overflow]")).reverse();
        buttonGroups.forEach((group) => group.classList.remove("overflow-hidden"));

        const nextOverflow = [];
        const availableWidth = Math.max(0, viewport.clientWidth - 54);

        for (const group of buttonGroups) {
            if (inline.scrollWidth <= availableWidth) {
                break;
            }
            nextOverflow.push(group.dataset.overflow);
            group.classList.add("overflow-hidden");
        }

        const normalized = new Set(nextOverflow.filter(Boolean));
        setOverflowGroups((current) => {
            const sameSize = current.size === normalized.size;
            const sameValues = sameSize && Array.from(current).every((value) => normalized.has(value));
            return sameValues ? current : normalized;
        });

        if (normalized.size === 0) {
            setOverflowOpen(false);
        }
    }, []);

    const scheduleToolbarOverflow = useCallback(() => {
        if (toolbarOverflowFrameRef.current !== null) {
            window.cancelAnimationFrame(toolbarOverflowFrameRef.current);
        }

        toolbarOverflowFrameRef.current = window.requestAnimationFrame(() => {
            toolbarOverflowFrameRef.current = null;
            buildToolbarOverflow();
        });
    }, [buildToolbarOverflow]);

    const remoteCursorExtension = useMemo(() => {
        if (!selectedNode?.id) {
            return [];
        }
        return buildCodePresenceExtension(remoteCursors);
    }, [remoteCursors, selectedNode?.id]);

    const isTextDocument = selectedNode?.node_type === "text";
    const isVisualMode = isTextDocument && currentEditorMode === "visual";
    const canShowVisualToggle = isTextDocument && (availableEditorModes || []).includes("visual");
    const [activeFigureData, setActiveFigureData] = useState(null);
    const [figureDraft, setFigureDraft] = useState(null);
    const [figureModalSource, setFigureModalSource] = useState(FigureModalSource.FILE_TREE);
    const [isFigureUploadPending, setIsFigureUploadPending] = useState(false);
    const [isMathOCRModalOpen, setIsMathOCRModalOpen] = useState(false);
    const {
        status: mathOCRStatus,
        latex: mathOCRLatex,
        errorMessage: mathOCRErrorMessage,
        isRunning: isMathOCRRunning,
        startRequest: startMathOCRRequest,
        stopRequest: stopMathOCRRequest,
        reset: resetMathOCR,
    } = useMathOCRWebSocket({
        spaceUuid,
        manuscriptId,
        nodeId: selectedNode?.node_type === "text" ? selectedNode.id : null,
        enabled: isMathOCRModalOpen && selectedNode?.node_type === "text",
    });
    const projectFigureFiles = useMemo(() => {
        const nodes = Array.isArray(manuscriptTreeData) ? manuscriptTreeData : [];

        return nodes
            .filter((node) => {
                const contentType = (node?.file_content_type || "").toLowerCase();
                const path = (node?.path_cache || node?.name || "").toLowerCase();

                if (node?.node_type !== "binary") {
                    return false;
                }

                return (
                    contentType.startsWith("image/") ||
                    contentType === "application/pdf" ||
                    [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".pdf"].some((ext) => path.endsWith(ext))
                );
            })
            .map((node) => ({
                id: node.id,
                name: node.name,
                path: node.path_cache || node.name || "",
                contentType: node.file_content_type || "",
            }))
            .sort((left, right) => left.path.localeCompare(right.path));
    }, [manuscriptTreeData]);
    const projectFolders = useMemo(() => {
        const nodes = Array.isArray(manuscriptTreeData) ? manuscriptTreeData : [];
        return nodes
            .filter((node) => node?.node_type === "folder")
            .map((node) => ({
                id: node.id,
                name: node.name,
                path: node.path_cache || node.name || "",
            }))
            .sort((left, right) => left.path.localeCompare(right.path));
    }, [manuscriptTreeData]);
    const previewByPath = useMemo(
        () => (path) => {
            if (!path) return null;

            const normalizedPath = normalizeGraphicsPath(path);
            const externalPath = isExternalGraphicsPath(normalizedPath);
            const matchingNode = resolveFigurePreviewNode(manuscriptTreeData, normalizedPath);
            const resolvedPath = matchingNode?.path_cache || matchingNode?.name || normalizedPath;
            const extensionSource = matchingNode ? resolvedPath : normalizedPath;
            const extension = extensionSource.includes(".") ? extensionSource.split(".").pop() : "";

            return {
                path,
                url: matchingNode?.file_url || (externalPath ? path : path.startsWith("/") ? path : `/${path}`),
                extension,
            };
        },
        [manuscriptTreeData],
    );

    const extensions = useMemo(
        () => [
            manuscriptLatex({ syntaxValidation }),
            EditorView.editable.of(!readOnly),
            autoComplete({ enabled: true }),
            metadataState,
            manuscriptSearchPhrases,
            EditorView.lineWrapping,
            search({
                top: true,
                closeOnBlur: false,
            }),
            keymap.of([
                {
                    key: "Mod-s",
                    run: () => {
                        if (saveDisabled) return true;
                        onSave?.();
                        return true;
                    },
                },
                {
                    key: "Mod-f",
                    run: (view) => {
                        openSearchPanel(view);
                        scheduleSearchMatchCounter(view);
                        return true;
                    },
                },
            ]),
            ...compileAnnotations(),
            latexSyntaxErrorClickExtension((payload) => {
                onLatexSyntaxErrorClick?.({
                    nodeId: selectedNode?.id || null,
                    ...payload,
                });
            }),
            ...selectionActionContextMenu(onOpenSelectionActionMenu),
            ...reviewThreadClickExtension(onSelectReviewThread),
            EditorView.theme({
                "&": {
                    height: "100%",
                },
                ".cm-content": {
                    caretColor: "#0f8f6f",
                },
            }),
            ...(realtimeExtension ? (Array.isArray(realtimeExtension) ? realtimeExtension : [realtimeExtension]) : []),
            ...remoteCursorExtension,
            ...manuscriptVisual({
                previewByPath,
                docId: selectedNode?.id || null,
                visual: isVisualMode,
            }),
        ],
        [
            isVisualMode,
            onOpenSelectionActionMenu,
            onLatexSyntaxErrorClick,
            onSave,
            onSelectReviewThread,
            previewByPath,
            readOnly,
            realtimeExtension,
            remoteCursorExtension,
            saveDisabled,
            selectedNode,
            syntaxValidation,
        ],
    );
    const editorSessionKey = `${selectedNode?.id || "none"}:${realtimeExtension ? "ot" : "plain"}`;
    const activeEditorView = currentEditorView?.sessionKey === editorSessionKey ? currentEditorView.view : null;
    const scrollTimerRef = useRef(null);

    const applyEditorMetadata = useCallback(
        (editorView) => {
            if (!editorView) {
                return;
            }

            editorView.dispatch(
                setMetadata({
                    selectedNode: selectedNode || null,
                    fileTreeData: manuscriptTreeData || [],
                    ...collectCompletionMetadata(editorContent),
                }),
            );
        },
        [editorContent, manuscriptTreeData, selectedNode],
    );

    const applyCompileAnnotationsToEditor = useCallback(
        (editorView) => {
            if (!editorView || selectedNode?.node_type !== "text") {
                return;
            }
            const enableCompileLogLinter = !syntaxValidation || (!editedSinceCompileStarted && !compiling);
            editorView.dispatch(
                setCompileAnnotations(editorView.state, compileLogAnnotations || []),
                showCompileLogDiagnostics(enableCompileLogLinter),
            );
        },
        [compileLogAnnotations, compiling, editedSinceCompileStarted, selectedNode, syntaxValidation],
    );

    const applySyntaxValidationToEditor = useCallback(
        (editorView) => {
            if (!editorView || selectedNode?.node_type !== "text") {
                return;
            }

            editorView.dispatch(setSyntaxValidation(syntaxValidation));
        },
        [selectedNode, syntaxValidation],
    );

    const textEditorBasicSetup = useMemo(
        () => ({
            drawSelection: false,
            foldGutter: !isVisualMode,
            highlightActiveLine: true,
            highlightActiveLineGutter: !isVisualMode,
            highlightSelectionMatches: false,
            lineNumbers: true,
            searchKeymap: true,
        }),
        [isVisualMode],
    );

    useEffect(() => {
        return () => {
            if (scrollTimerRef.current) {
                window.clearTimeout(scrollTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        scheduleToolbarOverflow();
    }, [scheduleToolbarOverflow, canShowVisualToggle, isTextDocument, isVisualMode, selectedNode?.id]);

    useEffect(() => {
        const viewport = toolbarViewportRef.current;
        const inline = toolbarInlineRef.current;
        if (!viewport || !inline) {
            return;
        }

        const handleResize = () => {
            scheduleToolbarOverflow();
        };

        let resizeObserver;
        if ("ResizeObserver" in window) {
            resizeObserver = new ResizeObserver(() => {
                scheduleToolbarOverflow();
            });
            resizeObserver.observe(viewport);
            resizeObserver.observe(inline);
        } else {
            window.addEventListener("resize", handleResize);
        }

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", handleResize);
        };
    }, [scheduleToolbarOverflow]);

    useEffect(() => {
        return () => {
            if (toolbarOverflowFrameRef.current !== null) {
                window.cancelAnimationFrame(toolbarOverflowFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!activeEditorView) {
            return;
        }
        applyEditorMetadata(activeEditorView);
    }, [activeEditorView, applyEditorMetadata]);

    useEffect(() => {
        if (!activeEditorView) {
            return;
        }
        applyCompileAnnotationsToEditor(activeEditorView);
    }, [activeEditorView, applyCompileAnnotationsToEditor]);

    useEffect(() => {
        if (!activeEditorView) {
            return;
        }
        applySyntaxValidationToEditor(activeEditorView);
    }, [activeEditorView, applySyntaxValidationToEditor]);

    useEffect(() => {
        if (!activeEditorView || selectedNode?.node_type !== "text") {
            return;
        }

        const cursorPos = activeEditorView.state.selection.main.head;
        const safePos = Math.max(0, Math.min(cursorPos, activeEditorView.state.doc.length));
        const linePos = activeEditorView.state.doc.lineAt(safePos).from;
        const shouldRestoreViewport = previousEditorModeRef.current !== currentEditorMode;
        const switchedToVisual = previousEditorModeRef.current !== currentEditorMode && isVisualMode;
        previousEditorModeRef.current = currentEditorMode;

        activeEditorView.dispatch(
            setVisual({
                docId: selectedNode?.id || null,
                previewByPath,
                visual: isVisualMode,
            }),
        );

        if (!shouldRestoreViewport) {
            return;
        }

        let animationFrameId = null;
        let fallbackTimerId = null;

        const jumpToCursorLine = () => {
            activeEditorView.dispatch({
                effects: EditorView.scrollIntoView(linePos, { y: "center" }),
            });
        };

        let handleScrollPositionRestored = null;

        if (switchedToVisual) {
            // Overleaf visual mode restores scroll again after parsing/decorations finish,
            // so code->visual cursor-line alignment has to wait for that restore cycle.
            handleScrollPositionRestored = () => {
                window.removeEventListener("editor:scroll-position-restored", handleScrollPositionRestored);
                if (fallbackTimerId !== null) {
                    window.clearTimeout(fallbackTimerId);
                    fallbackTimerId = null;
                }
                animationFrameId = window.requestAnimationFrame(jumpToCursorLine);
            };

            window.addEventListener("editor:scroll-position-restored", handleScrollPositionRestored);
            fallbackTimerId = window.setTimeout(() => {
                window.removeEventListener("editor:scroll-position-restored", handleScrollPositionRestored);
                animationFrameId = window.requestAnimationFrame(jumpToCursorLine);
            }, 250);
        } else {
            animationFrameId = window.requestAnimationFrame(jumpToCursorLine);
        }

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            if (fallbackTimerId !== null) {
                window.clearTimeout(fallbackTimerId);
            }
            if (handleScrollPositionRestored) {
                window.removeEventListener("editor:scroll-position-restored", handleScrollPositionRestored);
            }
        };
    }, [activeEditorView, currentEditorMode, isVisualMode, previewByPath, selectedNode]);

    useEffect(() => {
        const handleOpenFigureModal = () => {
            const editorView = editorRef?.current?.view;
            const figureData = editorView?.state?.field?.(editFigureData, false) || null;
            if (figureData) {
                setActiveFigureData(figureData);
                setFigureModalSource(FigureModalSource.EDIT_FIGURE);
                setFigureDraft(
                    createDefaultFigureDraft({
                        path: figureData.file?.path || "",
                        width: figureData.width ?? "",
                        includeCaption: figureData.caption !== null,
                        includeLabel: figureData.label !== null,
                        captionText: figureData.caption
                            ? extractCommandArgument(
                                  editorView.state.sliceDoc(figureData.caption.from, figureData.caption.to),
                              )
                            : "",
                        labelText: figureData.label
                            ? extractCommandArgument(
                                  editorView.state.sliceDoc(figureData.label.from, figureData.label.to),
                              )
                            : "",
                    }),
                );
            }
        };

        const handleOpenInsertFigureModal = (event) => {
            const detail = event?.detail || {};
            setActiveFigureData(null);
            setFigureModalSource(detail.source || FigureModalSource.FILE_TREE);
            setFigureDraft(
                createDefaultFigureDraft({
                    path: detail.filePath || "",
                }),
            );
        };

        const handlePasteImageFigureModal = (event) => {
            const detail = event?.detail || {};
            setActiveFigureData(null);
            setFigureModalSource(FigureModalSource.FILE_UPLOAD);
            setFigureDraft(
                createDefaultFigureDraft({
                    path: detail.name || "",
                }),
            );

            if (detail.data) {
                const scheduleUpload =
                    typeof queueMicrotask === "function"
                        ? queueMicrotask
                        : (callback) => window.setTimeout(callback, 0);
                scheduleUpload(() => {
                    void uploadFigureFile(detail.data);
                });
            }
        };

        window.addEventListener("figure-modal:open-modal", handleOpenFigureModal);
        window.addEventListener("figure-modal:open", handleOpenInsertFigureModal);
        window.addEventListener("figure-modal:paste-image", handlePasteImageFigureModal);
        return () => {
            window.removeEventListener("figure-modal:open-modal", handleOpenFigureModal);
            window.removeEventListener("figure-modal:open", handleOpenInsertFigureModal);
            window.removeEventListener("figure-modal:paste-image", handlePasteImageFigureModal);
        };
    }, [editorRef]);

    const closeFigureModal = () => {
        const editorView = editorRef?.current?.view;
        if (editorView) {
            editorView.dispatch({
                effects: editFigureDataEffect.of(null),
            });
        }
        setActiveFigureData(null);
        setFigureDraft(null);
        setFigureModalSource(FigureModalSource.FILE_TREE);
    };

    const applyFigureModal = async () => {
        const editorView = editorRef?.current?.view;
        const figure = editorView?.state?.field?.(editFigureData, false);

        if (!editorView || !figureDraft) {
            return;
        }

        let nextPath = (figureDraft.path || "").trim();
        const nextWidth =
            figureDraft.width === "" || figureDraft.width === null ? undefined : Number(figureDraft.width);
        const includeCaption = Boolean(figureDraft.includeCaption);
        const includeLabel = Boolean(figureDraft.includeLabel);
        const captionCommand = `\\caption{${figureDraft.captionText || "Enter Caption"}}`;
        const labelCommand = `\\label{${figureDraft.labelText || "fig:placeholder"}}`;

        if (figureModalSource === FigureModalSource.FROM_URL && /^https?:\/\//i.test(nextPath)) {
            if (!importFigureUrl) {
                setToast?.("URL 이미지를 가져올 수 없습니다.", "error");
                return;
            }

            const waitForTreeUpdate = waitForFileTreeUpdate(editorView).withTimeout(1500);
            setIsFigureUploadPending(true);
            try {
                const createdNode = await importFigureUrl(nextPath, undefined, { conflictPolicy: "rename" });
                nextPath = createdNode?.path_cache || createdNode?.name || nextPath;
                await waitForTreeUpdate;
                setToast?.("URL 이미지가 프로젝트 파일로 저장되었습니다.", "info");
            } catch (error) {
                setToast?.(
                    error.response?.data?.url?.[0] ||
                        error.response?.data?.name?.[0] ||
                        "URL 이미지 가져오기 중 오류가 발생했습니다.",
                    "error",
                );
                return;
            } finally {
                setIsFigureUploadPending(false);
            }
        }

        if (!figure) {
            const nextIsSvg = isSvgFile(nextPath);
            const insertPath = nextIsSvg ? nextPath.replace(/\.svg$/i, "") : nextPath;
            const graphicsCommandName = nextIsSvg ? "includesvg" : "includegraphics";
            const widthArgument =
                nextWidth !== undefined && !Number.isNaN(nextWidth) ? `[width=${nextWidth}\\linewidth]` : "";
            const captionLine = includeCaption ? `\n\\caption{${figureDraft.captionText || "Enter Caption"}}` : "";
            const labelLine = includeLabel ? `\n\\label{${figureDraft.labelText || "fig:placeholder"}}` : "";
            const selection = editorView.state.selection.main;
            const insert = `\\begin{figure}\n\\centering\n\\${graphicsCommandName}${widthArgument}{${insertPath}}${captionLine}${labelLine}\n\\end{figure}`;

            editorView.dispatch({
                changes: {
                    from: selection.from,
                    to: selection.to,
                    insert,
                },
                effects: editFigureDataEffect.of(null),
                scrollIntoView: true,
            });
            setActiveFigureData(null);
            setFigureDraft(null);
            return;
        }

        const changes = [];

        if (!figure.caption && includeCaption) {
            changes.push({
                from: figure.graphicsCommand.to,
                insert: `\n${captionCommand}`,
            });
        } else if (figure.caption && !includeCaption) {
            changes.push({
                from: figure.caption.from,
                to: figure.caption.to,
                insert: "",
            });
        } else if (figure.caption && includeCaption) {
            changes.push({
                from: figure.caption.from,
                to: figure.caption.to,
                insert: captionCommand,
            });
        }

        if (!figure.label && includeLabel) {
            changes.push({
                from: figure.label?.to ?? figure.caption?.to ?? figure.graphicsCommand.to,
                insert: `\n${labelCommand}`,
            });
        } else if (figure.label && !includeLabel) {
            changes.push({
                from: figure.label.from,
                to: figure.label.to,
                insert: "",
            });
        } else if (figure.label && includeLabel) {
            changes.push({
                from: figure.label.from,
                to: figure.label.to,
                insert: labelCommand,
            });
        }

        if (nextPath) {
            const nextIsSvg = isSvgFile(nextPath);
            const wasSvg = isSvgFile(figure.file?.path);
            const insertPath = nextIsSvg ? nextPath.replace(/\.svg$/i, "") : nextPath;
            const widthArgument =
                nextWidth !== undefined && !Number.isNaN(nextWidth)
                    ? `[width=${nextWidth}\\linewidth]`
                    : figure.unknownGraphicsArguments
                      ? `[${figure.unknownGraphicsArguments}]`
                      : "";

            if (nextIsSvg !== wasSvg) {
                const graphicsCommandName = nextIsSvg ? "includesvg" : "includegraphics";
                changes.push({
                    from: figure.graphicsCommand.from,
                    to: figure.graphicsCommand.to,
                    insert: `\\${graphicsCommandName}${widthArgument}{${insertPath}}`,
                });
            } else {
                if (figure.graphicsCommandArguments && nextWidth !== undefined && !Number.isNaN(nextWidth)) {
                    changes.push({
                        from: figure.graphicsCommandArguments.from,
                        to: figure.graphicsCommandArguments.to,
                        insert: `width=${nextWidth}\\linewidth`,
                    });
                } else if (!figure.graphicsCommandArguments && nextWidth !== undefined && !Number.isNaN(nextWidth)) {
                    changes.push({
                        from: figure.file.from - 1,
                        insert: `[width=${nextWidth}\\linewidth]`,
                    });
                }

                changes.push({
                    from: figure.file.from,
                    to: figure.file.to,
                    insert: insertPath,
                });
            }
        }

        changes.sort((a, b) => b.from - a.from);
        editorView.dispatch({
            changes,
            effects: editFigureDataEffect.of(null),
        });
        setActiveFigureData(null);
        setFigureDraft(null);
        setFigureModalSource(FigureModalSource.FILE_TREE);
    };

    const uploadFigureFile = async (file, parentId) => {
        if (!file || !uploadBinaryFile) {
            return;
        }

        const editorView = editorRef?.current?.view;
        const waitForTreeUpdate = editorView ? waitForFileTreeUpdate(editorView).withTimeout(1500) : Promise.resolve();

        setIsFigureUploadPending(true);
        try {
            const createdNode = await uploadBinaryFile(file, parentId, { conflictPolicy: "rename" });
            const nextPath = createdNode?.path_cache || createdNode?.name || file.name || "";
            const inferred = inferFigureTextFromPath(nextPath, figureDraft || {});

            setFigureDraft((current) =>
                createDefaultFigureDraft({
                    ...current,
                    path: nextPath,
                    ...inferred,
                }),
            );

            await waitForTreeUpdate;
            setToast?.("그림 파일이 업로드되었습니다.", "info");
        } catch (error) {
            setToast?.(error.response?.data?.file?.[0] || "그림 파일 업로드 중 오류가 발생했습니다.", "error");
        } finally {
            setIsFigureUploadPending(false);
        }
    };

    const deleteFigureModal = () => {
        const editorView = editorRef?.current?.view;
        const figure = editorView?.state?.field?.(editFigureData, false);
        if (!editorView || !figure) {
            return;
        }

        editorView.dispatch({
            changes: {
                from: figure.from,
                to: figure.to,
                insert: "",
            },
            effects: editFigureDataEffect.of(null),
        });
        setActiveFigureData(null);
        setFigureDraft(null);
        setFigureModalSource(FigureModalSource.FILE_TREE);
    };

    useEffect(() => {
        const handleGlobalFind = (event) => {
            if (!(event.metaKey || event.ctrlKey)) return;
            if (event.key.toLowerCase() !== "f") return;
            if (selectedNode?.node_type !== "text") return;

            event.preventDefault();
            const editorView = editorRef?.current?.view;
            if (!editorView) return;
            editorView.focus();
            openSearchPanel(editorView);
            scheduleSearchMatchCounter(editorView);
        };

        window.addEventListener("keydown", handleGlobalFind);
        return () => {
            window.removeEventListener("keydown", handleGlobalFind);
        };
    }, [editorRef, selectedNode]);

    const runEditorCommand = (command) => {
        const editorView = editorRef?.current?.view;
        if (!editorView) return;
        editorView.focus();
        command(editorView);
        scheduleSearchMatchCounter(editorView);
    };

    const openMathOCRModal = () => {
        if (!isTextDocument) {
            setToast?.("텍스트 문서에서만 수식 OCR을 사용할 수 있습니다.", "info");
            return;
        }
        resetMathOCR();
        setIsMathOCRModalOpen(true);
    };

    const closeMathOCRModal = () => {
        if (isMathOCRRunning) {
            stopMathOCRRequest();
        }
        resetMathOCR();
        setIsMathOCRModalOpen(false);
    };

    const insertMathOCRLatex = (latex) => {
        const editorView = editorRef?.current?.view;
        const normalizedLatex = String(latex || "").trim();
        if (!editorView || !normalizedLatex) {
            return;
        }

        const selectionRange = editorView.state.selection.main;
        const insert = `\n\\[\n${normalizedLatex}\n\\]\n`;
        editorView.focus();
        editorView.dispatch({
            changes: {
                from: selectionRange.from,
                to: selectionRange.to,
                insert,
            },
            selection: {
                anchor: selectionRange.from + insert.length,
                head: selectionRange.from + insert.length,
            },
            scrollIntoView: true,
        });
        scheduleSearchMatchCounter(editorView);
        setToast?.("수식 OCR 결과를 삽입했습니다.", "info");
        closeMathOCRModal();
    };

    useEffect(() => {
        if (!externalCommand?.type) return;
        if (selectedNode?.node_type !== "text") return;

        if (externalCommand.type === "undo") {
            runEditorCommand(undo);
            return;
        }
        if (externalCommand.type === "redo") {
            runEditorCommand(redo);
            return;
        }
        if (externalCommand.type === "search") {
            runEditorCommand(openSearchPanel);
            return;
        }
        if (externalCommand.type === "focusLine") {
            const editorView = editorRef?.current?.view;
            const lineNumber = Number.parseInt(externalCommand.lineNumber, 10);
            if (!editorView || !Number.isFinite(lineNumber)) return;

            const totalLines = editorView.state.doc.lines || 1;
            const clampedLine = Math.max(1, Math.min(lineNumber, totalLines));
            const lineInfo = editorView.state.doc.line(clampedLine);

            editorView.focus();
            editorView.dispatch({
                selection: { anchor: lineInfo.from, head: lineInfo.from },
                effects: EditorView.scrollIntoView(lineInfo.from, { y: "center" }),
            });
        }
    }, [externalCommand, selectedNode]);

    const handleEditorUpdate = (viewUpdate) => {
        updateSearchMatchCounter(viewUpdate.view);

        if (viewUpdate.selectionSet) {
            const mainSelection = viewUpdate.state.selection.main;
            const head = mainSelection.head;
            const anchor = mainSelection.anchor;
            const line = viewUpdate.state.doc.lineAt(head);
            const from = Math.min(anchor, head);
            const to = Math.max(anchor, head);
            const startLine = viewUpdate.state.doc.lineAt(from);
            const endLine = viewUpdate.state.doc.lineAt(to);
            onSelectionChange?.({
                line: line.number,
                column: head - line.from + 1,
                from,
                to,
                hasSelection: from !== to,
                startLine: startLine.number,
                startColumn: from - startLine.from + 1,
                endLine: endLine.number,
                endColumn: to - endLine.from + 1,
                selectedText: from !== to ? viewUpdate.state.sliceDoc(from, to) : "",
            });
            onActiveLineChange?.(line.number);
        }

        if (viewUpdate.viewportChanged) {
            if (scrollTimerRef.current) {
                window.clearTimeout(scrollTimerRef.current);
            }
            scrollTimerRef.current = window.setTimeout(() => {
                const { from, to } = viewUpdate.view.viewport;
                const middlePos = Math.floor((from + to) / 2);
                const middleLine = viewUpdate.state.doc.lineAt(Math.max(0, middlePos)).number;
                onActiveLineChange?.(middleLine);
            }, 120);
        }
    };

    const breadcrumbSegments = useMemo(() => {
        const segments = [];
        if (selectedNode?.name) {
            segments.push({
                id: `file-${selectedNode.id}`,
                label: selectedNode.name,
                root: !!selectedNode.is_root,
                line: 1,
            });
        }
        (currentLocationPath || []).forEach((item) => {
            segments.push({
                id: item.id,
                label: item.title,
                root: false,
                line: item.line,
            });
        });
        return segments;
    }, [currentLocationPath, selectedNode]);

    const isImageBinary = useMemo(() => {
        if (selectedNode?.node_type !== "binary") return false;
        const contentType = (selectedNode.file_content_type || "").toLowerCase();
        const name = (selectedNode.name || "").toLowerCase();
        return (
            contentType.startsWith("image/") ||
            [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].some((ext) => name.endsWith(ext))
        );
    }, [selectedNode]);

    const openInsertFigureModal = (source) => {
        const nextSource = source || (isImageBinary ? FigureModalSource.FILE_TREE : FigureModalSource.FILE_UPLOAD);
        window.dispatchEvent(
            new CustomEvent("figure-modal:open", {
                detail: {
                    source: nextSource,
                    filePath:
                        nextSource === FigureModalSource.FILE_TREE && isImageBinary
                            ? selectedNode?.path_cache || selectedNode?.name || ""
                            : "",
                },
            }),
        );
    };

    return (
        <Panel>
            <EditorToolbar>
                <ToolbarGroup ref={toolbarViewportRef}>
                    <ToolbarInline ref={toolbarInlineRef}>
                        <ManuscriptEditorToolbar
                            Button={Button}
                            editorRef={editorRef}
                            editorContent={editorContent}
                            selection={selection}
                            selectedNode={selectedNode}
                            isTextDocument={isTextDocument}
                            isVisualMode={isVisualMode}
                            canShowVisualToggle={canShowVisualToggle}
                            onEditorModeChange={onEditorModeChange}
                            runEditorCommand={runEditorCommand}
                            onOpenInsertFigureModal={openInsertFigureModal}
                            onOpenMathOCR={openMathOCRModal}
                            setToast={setToast}
                        />
                    </ToolbarInline>
                    <ToolbarOverflowSpacer />
                    <OverflowAnchor $visible={overflowGroups.size > 0}>
                        <Dropdown
                            open={overflowOpen}
                            onOpenChange={setOverflowOpen}
                            placement="bottom-end"
                            offset={6}
                            contentMinWidth="280px"
                            anchor={
                                <Tooltip message="더보기">
                                    <Button
                                        type="button"
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onClick={() => setOverflowOpen((prev) => !prev)}
                                        aria-label="더보기"
                                    >
                                        <Icon name={"submenu"} size={"14px"} color={"#42576b"} />
                                    </Button>
                                </Tooltip>
                            }
                        >
                            {overflowOpen && overflowGroups.size > 0 ? (
                                <OverflowMenu role="group" aria-label="더보기 도구 모음">
                                    <ManuscriptEditorToolbar
                                        Button={Button}
                                        editorRef={editorRef}
                                        editorContent={editorContent}
                                        selection={selection}
                                        selectedNode={selectedNode}
                                        isTextDocument={isTextDocument}
                                        isVisualMode={isVisualMode}
                                        canShowVisualToggle={canShowVisualToggle}
                                        onEditorModeChange={onEditorModeChange}
                                        runEditorCommand={runEditorCommand}
                                        onOpenInsertFigureModal={openInsertFigureModal}
                                        onOpenMathOCR={openMathOCRModal}
                                        setToast={setToast}
                                        overflowed={overflowGroups}
                                    />
                                </OverflowMenu>
                            ) : null}
                        </Dropdown>
                    </OverflowAnchor>
                </ToolbarGroup>
            </EditorToolbar>
            <LocationBar>
                {breadcrumbSegments.length ? (
                    breadcrumbSegments.map((segment, index) => (
                        <React.Fragment key={segment.id}>
                            {index > 0 ? <LocationDivider>{">"}</LocationDivider> : null}
                            <LocationSegment
                                type="button"
                                $active={index === breadcrumbSegments.length - 1}
                                onClick={index > 0 ? () => onLocationSelect?.(segment.line || 1) : undefined}
                            >
                                {index === 0 ? <Icon name={"memo"} size={"11px"} color={"#64748b"} /> : null}
                                <LocationLabel>{segment.label}</LocationLabel>
                                {segment.root ? <Status>Root</Status> : null}
                            </LocationSegment>
                        </React.Fragment>
                    ))
                ) : (
                    <LocationSegment type="button" $active>
                        <LocationLabel>문서를 선택하세요.</LocationLabel>
                    </LocationSegment>
                )}
            </LocationBar>
            <EditorArea>
                {selectedNode?.node_type === "text" ? (
                    <ManuscriptTextEditorHost
                        editorRef={editorRef}
                        editorContent={editorContent}
                        editorSessionKey={editorSessionKey}
                        extensions={extensions}
                        basicSetup={textEditorBasicSetup}
                        isRealtime={!!realtimeExtension}
                        onCreateEditor={setCurrentEditorView}
                        onEditorChange={readOnly ? undefined : onEditorChange}
                        onUpdate={handleEditorUpdate}
                    />
                ) : selectedNode?.node_type === "binary" ? (
                    isImageBinary && selectedNode.file_url ? (
                        <ImageViewer>
                            <ImageFrame>
                                <PreviewImage src={selectedNode.file_url} alt={selectedNode.name} />
                            </ImageFrame>
                        </ImageViewer>
                    ) : (
                        <Placeholder>
                            <Icon name={"file"} size={"28px"} color={"#0f8f6f"} />
                            <div>바이너리 파일은 직접 편집할 수 없습니다.</div>
                            <MetaList>
                                <MetaKey>파일명</MetaKey>
                                <MetaValue>{selectedNode.name}</MetaValue>
                                <MetaKey>경로</MetaKey>
                                <MetaValue>{selectedNode.path_cache}</MetaValue>
                                {selectedNode.file_content_type ? (
                                    <>
                                        <MetaKey>형식</MetaKey>
                                        <MetaValue>{selectedNode.file_content_type}</MetaValue>
                                    </>
                                ) : null}
                            </MetaList>
                        </Placeholder>
                    )
                ) : (
                    <Placeholder>
                        <Icon name={"file"} size={"24px"} color={"#8ea4bb"} />
                        <div>왼쪽 트리에서 문서를 선택하세요.</div>
                    </Placeholder>
                )}
                <ManuscriptFigureModal
                    activeFigureData={activeFigureData}
                    figureDraft={figureDraft}
                    figureModalSource={figureModalSource}
                    isFigureUploadPending={isFigureUploadPending}
                    isImageBinary={isImageBinary}
                    projectFigureFiles={projectFigureFiles}
                    projectFolders={projectFolders}
                    onClose={closeFigureModal}
                    onDelete={deleteFigureModal}
                    onApply={applyFigureModal}
                    onDraftChange={setFigureDraft}
                    onUploadFile={uploadFigureFile}
                />
                <ManuscriptMathOCRModal
                    open={isMathOCRModalOpen}
                    status={mathOCRStatus}
                    latex={mathOCRLatex}
                    errorMessage={mathOCRErrorMessage}
                    isRunning={isMathOCRRunning}
                    onRequest={startMathOCRRequest}
                    onStop={stopMathOCRRequest}
                    onInsert={insertMathOCRLatex}
                    onClose={closeMathOCRModal}
                    onReset={resetMathOCR}
                />
            </EditorArea>
            <StatusBar>
                <div>
                    Ln {selection.line}, Col {selection.column}
                </div>
                <div>
                    {selectedNode?.node_type === "text"
                        ? `${editorContent.length} chars${saveLabel ? ` · ${saveLabel}` : ""}`
                        : null}
                    {currentVersion ? ` · v${currentVersion}` : ""}
                </div>
            </StatusBar>
        </Panel>
    );
}

export default ManuscriptEditorPanel;
